import React, { useState, useEffect, useRef } from 'react';
import { userAPI, adAPI } from '../services/api';
import { useAuth } from '../hooks/useAuth';
import { useTranslation } from 'react-i18next';
import { AdMob, RewardAdOptions, RewardAdPluginEvents } from '@capacitor-community/admob';
import { Purchases, LOG_LEVEL, PRODUCT_CATEGORY } from '@revenuecat/purchases-capacitor';
import { Capacitor } from '@capacitor/core';

interface CoinPackage {
  id: string;
  name: string;
  coins: number;
  prices: { [key: string]: number }; // Store prices for different currencies
  bonus: number;
  popular?: boolean;
  icon: string;
  description: string;
}

interface Transaction {
  id: string;
  type: 'purchase' | 'earned' | 'spent';
  amount: number;
  description: string;
  timestamp: Date;
}

const JetonPage: React.FC = () => {
  const { t, i18n } = useTranslation();
  const [watchedToday, setWatchedToday] = useState(0);
  const [maxDailyAds] = useState(5);
  const [adReward] = useState(5);
  const [showAdModal, setShowAdModal] = useState(false);
  const [isAdLoading, setIsAdLoading] = useState(false);
  const [adWatched, setAdWatched] = useState(false);
  const [showCamera, setShowCamera] = useState(false);
  const [photoCaption, setPhotoCaption] = useState('');
  const [transactions, setTransactions] = useState<Transaction[]>([]);
  const [selectedPackage, setSelectedPackage] = useState<CoinPackage | null>(null);
  const videoRef = useRef<HTMLVideoElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const { user, purchaseVip, isVipActive, getVipDaysLeft, addCoins, updateUser } = useAuth();
  
  const coins = user?.coins || 0;

  // Helper to determine currency and symbol based on language
  const getCurrencyInfo = () => {
    const lang = i18n.language;
    if (['tr'].includes(lang)) return { code: 'TRY', symbol: '₺', rate: 1 }; // Base rate (relative logic)
    if (['de', 'fr', 'it', 'es'].includes(lang)) return { code: 'EUR', symbol: '€', rate: 0.035 }; // Approx rate
    return { code: 'USD', symbol: '$', rate: 0.03 }; // Approx rate
  };

  const currency = getCurrencyInfo();

  // Define packages with fixed prices for each region (Tiered Pricing)
  const coinPackages: CoinPackage[] = [
    {
      id: 'starter',
      name: 'Başlangıç',
      coins: 100,
      prices: { TRY: 24.99, USD: 0.99, EUR: 0.99 },
      bonus: 0,
      icon: '🪙',
      description: 'Küçük başlangıç için'
    },
    {
      id: 'basic',
      name: 'Temel',
      coins: 250,
      prices: { TRY: 49.99, USD: 2.99, EUR: 2.99 },
      bonus: 20, // Reduced from 25
      icon: '💰',
      description: 'Popüler seçenek'
    },
    {
      id: 'popular',
      name: 'Popüler',
      coins: 500,
      prices: { TRY: 99.99, USD: 4.99, EUR: 4.99 },
      bonus: 60, // Reduced from 75
      popular: true,
      icon: '💎',
      description: 'En çok tercih edilen'
    },
    {
      id: 'premium',
      name: 'Premium',
      coins: 1000,
      prices: { TRY: 174.99, USD: 9.99, EUR: 9.99 },
      bonus: 160, // Reduced from 200
      icon: '👑',
      description: 'Premium deneyim'
    },
    {
      id: 'vip',
      name: 'VIP',
      coins: 2500,
      prices: { TRY: 374.99, USD: 19.99, EUR: 19.99 },
      bonus: 400, // Reduced from 500
      icon: '🏆',
      description: 'VIP ayrıcalığı'
    },
    {
      id: 'legendary',
      name: 'Efsane',
      coins: 5000,
      prices: { TRY: 624.99, USD: 39.99, EUR: 39.99 },
      bonus: 1200, // Reduced from 1500
      icon: '🐉',
      description: 'Efsanevi paket'
    },
    {
      id: 'ultimate',
      name: 'Kral',
      coins: 20000,
      prices: { TRY: 2499.99, USD: 79.99, EUR: 79.99 },
      bonus: 4000, // High bonus for whales
      icon: '🔱',
      description: 'Sınırları zorla'
    }
  ];

  // VIP Logic
  const vipPrices = { TRY: 299.99, USD: 14.99, EUR: 14.99 };
  const currentVipPrice = vipPrices[currency.code as keyof typeof vipPrices] || vipPrices.USD;

  useEffect(() => {
    let listeners: any[] = [];

    const initializeAdMob = async () => {
      try {
        await AdMob.initialize({});
        
        const rewardedListener = await AdMob.addListener(RewardAdPluginEvents.Rewarded, async (rewardItem) => {
          setAdWatched(true);
          try {
            const result = await adAPI.watchAd();
            setWatchedToday(result.adsWatchedToday);
            updateUser({ coins: result.totalCoins });

            const newTransaction: Transaction = {
              id: Date.now().toString(),
              type: 'earned',
              amount: result.coinsEarned,
              description: 'Reklam izlendi',
              timestamp: new Date()
            };

            setTransactions(prev => [newTransaction, ...prev]);
          } catch (err) {
            console.error('Reklam ödülü backend\'e kaydedilemedi', err);
          }
        });
        listeners.push(rewardedListener);

        const dismissedListener = await AdMob.addListener(RewardAdPluginEvents.Dismissed, () => {
          setIsAdLoading(false);
          setShowAdModal(false);
          setAdWatched(false);
        });
        listeners.push(dismissedListener);

        const failedListener = await AdMob.addListener(RewardAdPluginEvents.FailedToLoad, (error) => {
          console.error('Reklam yüklenemedi:', error);
          setIsAdLoading(false);
          setShowAdModal(false);
        });
        listeners.push(failedListener);

      } catch (error) {
        console.error('AdMob başlatılamadı:', error);
      }
    };

    initializeAdMob();

    return () => {
      listeners.forEach(listener => {
        if (listener && listener.remove) {
          listener.remove();
        }
      });
    };
  }, [adReward]);

  useEffect(() => {
    const initRevenueCat = async () => {
      try {
        await Purchases.setLogLevel({ level: LOG_LEVEL.DEBUG });
        await Purchases.configure({ apiKey: 'goog_fsBTaUBYBcIbLufmebgxrKHcoUz' });
      } catch (e) {
        console.error('RevenueCat başlatılamadı:', e);
      }
    };
    initRevenueCat();
    
    // Fetch user ad status
    adAPI.getAdStatus().then(status => {
      setWatchedToday(status.adsWatchedToday);
    }).catch(console.error);
  }, []);

  useEffect(() => {
    const mockTransactions: Transaction[] = [
      {
        id: '1',
        type: 'earned',
        amount: 50,
        description: 'Yayından kazanıldı',
        timestamp: new Date(Date.now() - 3600000)
      },
      {
        id: '2',
        type: 'spent',
        amount: 25,
        description: 'Hediye gönderildi',
        timestamp: new Date(Date.now() - 7200000)
      },
      {
        id: '3',
        type: 'purchase',
        amount: 500,
        description: 'Popüler Paket satın alındı',
        timestamp: new Date(Date.now() - 86400000)
      }
    ];

    setTransactions(mockTransactions);
  }, []);

  const getCurrentPrice = (pkg: CoinPackage) => {
    // @ts-ignore
    return pkg.prices[currency.code] || pkg.prices.USD;
  };

  const purchaseCoins = async () => {
    if (!selectedPackage) return;
    const productId = `coin_${selectedPackage.id}`;

    try {
      if (Capacitor.getPlatform() === 'web') {
        console.log('Web ortamında test simülasyonu başlatılıyor...');
        // Simülasyon
        const totalCoins = selectedPackage.coins + selectedPackage.bonus;
        await addCoins(totalCoins);
        const newTransaction: Transaction = {
          id: Date.now().toString(),
          type: 'purchase',
          amount: totalCoins,
          description: `${selectedPackage.name} Paket (Web Simülasyon)`,
          timestamp: new Date()
        };
        setTransactions(prev => [newTransaction, ...prev]);
        setSelectedPackage(null);
        alert('Satın alma başarılı (Simülasyon)! Jetonlarınız eklendi.');
        return;
      }

      // Gerçek Cihaz (Google Play / App Store) RevenueCat Akışı
      const { products } = await Purchases.getProducts({ 
        productIdentifiers: [productId],
        type: PRODUCT_CATEGORY.NON_SUBSCRIPTION
      });
      
      if (!products || products.length === 0) {
        alert('Ürün bulunamadı. Lütfen Google Play Console üzerinden ürünlerin onaylandığından emin olun.');
        return;
      }

      await Purchases.purchaseStoreProduct({ product: products[0] });

      const totalCoins = selectedPackage.coins + selectedPackage.bonus;
      await addCoins(totalCoins);

      const newTransaction: Transaction = {
        id: Date.now().toString(),
        type: 'purchase',
        amount: totalCoins,
        description: `${selectedPackage.name} Paket (Google Play)`,
        timestamp: new Date()
      };

      setTransactions(prev => [newTransaction, ...prev]);
      setSelectedPackage(null);
      
      alert('Satın alma başarılı! Jetonlarınız eklendi.');

    } catch (error: any) {
      if (!error.userCancelled) {
        alert('Satın alma sırasında hata: ' + error.message);
      }
      console.error('Purchase error:', error);
    }
  };


  const handleVipPurchase = async () => {
    try {
      if (Capacitor.getPlatform() === 'web') {
        console.log('Web ortamında test simülasyonu (VIP) başlatılıyor...');
        await purchaseVip();
        alert('VIP Üyeliğiniz başarıyla başlatıldı (Simülasyon)!');
        return;
      }

      // RevenueCat flow
      const { products } = await Purchases.getProducts({ productIdentifiers: ['vip_subscription:vip-monthly'] });
      
      if (!products || products.length === 0) {
        alert('VIP paketi bulunamadı. Lütfen Google Play Console ayarlarını kontrol edin.');
        return;
      }
      await Purchases.purchaseStoreProduct({ product: products[0] });
      
      // Grant VIP status on success
      await purchaseVip();
      
      alert('VIP Üyeliğiniz başarıyla başlatıldı!');
    } catch (error: any) {
      alert('Satın alma sırasında hata: ' + error.message);
      console.error('VIP Purchase error:', error);
    }
  };

  const watchAd = async () => {
    if (watchedToday >= maxDailyAds || isAdLoading) return;

    setIsAdLoading(true);
    
    try {
      const options: RewardAdOptions = {
        adId: 'ca-app-pub-8602728792191733/5233996403',
        // isTesting: true, // You can set this to true during development
      };

      await AdMob.prepareRewardVideoAd(options);
      await AdMob.showRewardVideoAd();
    } catch (error) {
      console.error('Reklam gösterilemedi (AdMob). Simülasyon başlatılıyor...', error);
      
      // Simülasyon (Web / Test ortamı için)
      setTimeout(async () => {
         setAdWatched(true);
         try {
           const result = await adAPI.watchAd();
           setWatchedToday(result.adsWatchedToday);
           updateUser({ coins: result.totalCoins });

           const newTransaction = {
             id: Date.now().toString(),
             type: 'earned' as const,
             amount: result.coinsEarned,
             description: 'Reklam izlendi (Simülasyon)',
             timestamp: new Date()
           };

           setTransactions(prev => [newTransaction, ...prev]);
           setIsAdLoading(false);
           alert('Reklam başarıyla izlendi (Simülasyon) ve jeton eklendi!');
         } catch (err) {
           setIsAdLoading(false);
           alert('Günlük reklam sınırına ulaştınız!');
         }
      }, 1500);
    }
  };

  const closeAdModal = () => {
    if (!adWatched) {
      // Reklam izlenmeden kapatırsa coin verilmez
      setIsAdLoading(false);
    }
    setShowAdModal(false);
    setAdWatched(false);
  };

  const formatPrice = (price: number, isVip: boolean = false) => {
    const discountedPrice = isVip ? price * 0.9 : price; // %10 indirim
    return `${currency.symbol}${discountedPrice.toFixed(2)}`;
  };

  const getOriginalPrice = (price: number, isVip: boolean = false) => {
    const discountedPrice = isVip ? price * 0.9 : price;
    return {
      original: `${currency.symbol}${price.toFixed(2)}`,
      discounted: `${currency.symbol}${discountedPrice.toFixed(2)}`,
      savings: isVip ? `${currency.symbol}${(price * 0.1).toFixed(2)}` : null
    };
  };

  const formatAmount = (amount: number) => {
    return amount.toLocaleString('tr-TR');
  };

  return (
    <div style={styles.container}>
      {/* Header */}
      <div style={styles.header}>
        <div style={styles.headerContent}>
          <h2 style={styles.title}>
            <span style={styles.titleIcon}>🪙</span>
            Mağaza & Jetonlar
          </h2>
          <p style={styles.subtitle}>Bakiyeni yükle, ayrıcalıkların tadını çıkar</p>
        </div>
        <div style={styles.balanceCard}>
          <div style={styles.balanceInfo}>
            <span style={styles.balanceLabel}>Mevcut Bakiye</span>
            <span style={styles.balanceAmount}>{formatAmount(coins)}</span>
          </div>
          <div style={styles.coinIconWrapper}>
            <span style={styles.balanceIcon}>🪙</span>
            <div style={styles.coinGlow} />
          </div>
        </div>
      </div>

      {/* Reklam İzleme Bölümü */}
      <div style={styles.glassSection}>
        <div style={styles.sectionHeader}>
          <span style={styles.sectionIcon}>📺</span>
          <h3 style={styles.sectionTitle}>Reklam İzle & Kazan</h3>
        </div>
        <div style={styles.adContent}>
          <div style={styles.adLeft}>
            <p style={styles.adDescription}>
              Günlük {maxDailyAds} reklam izleyerek toplam <span style={styles.highlight}>{maxDailyAds * adReward} jeton</span> kazanabilirsin.
            </p>
            <div style={styles.adStats}>
              <div style={styles.adStatItem}>
                <span style={styles.adStatValue}>{maxDailyAds - watchedToday}</span>
                <span style={styles.adStatLabel}>Kalan Hak</span>
              </div>
              <div style={styles.statDivider} />
              <div style={styles.adStatItem}>
                <span style={styles.adStatValue}>+{adReward}</span>
                <span style={styles.adStatLabel}>Ödül</span>
              </div>
            </div>
          </div>

          <div style={styles.adRight}>
            <div style={styles.adProgressContainer}>
              <div style={styles.progressBar}>
                <div style={{
                  ...styles.progressFill,
                  width: `${(watchedToday / maxDailyAds) * 100}%`
                }} />
              </div>
              <span style={styles.progressText}>{watchedToday}/{maxDailyAds}</span>
            </div>

            <button
              onClick={watchAd}
              disabled={watchedToday >= maxDailyAds || isAdLoading}
              style={{
                ...styles.watchAdButton,
                opacity: watchedToday >= maxDailyAds ? 0.6 : 1,
                cursor: watchedToday >= maxDailyAds || isAdLoading ? 'not-allowed' : 'pointer',
                background: watchedToday >= maxDailyAds
                  ? 'rgba(255,255,255,0.1)'
                  : 'linear-gradient(45deg, #FF512F 0%, #DD2476 100%)'
              }}
            >
              {isAdLoading ? (
                <>
                  <div style={styles.spinner} />
                  <span>Yükleniyor...</span>
                </>
              ) : watchedToday >= maxDailyAds ? (
                <>
                  <span>✅ Tamamlandı</span>
                </>
              ) : (
                <>
                  <span>▶️ Reklam İzle (+{adReward})</span>
                </>
              )}
            </button>
          </div>
        </div>
      </div>

      <div style={styles.packagesSection}>
        <h3 style={styles.sectionTitle}>
          <span style={styles.sectionIcon}>🛒</span> {t('coins.packages', 'Jeton Paketleri')}
        </h3>
        <div style={styles.packagesGrid}>
          {coinPackages.map(pkg => {
            const currentPrice = getCurrentPrice(pkg);
            const pricing = getOriginalPrice(currentPrice, isVipActive());
            return (
              <button
                key={pkg.id}
                onClick={() => setSelectedPackage(pkg)}
                style={{
                  ...styles.packageCard,
                  border: selectedPackage?.id === pkg.id ? '2px solid #ff007f' : '1px solid rgba(255,255,255,0.1)',
                  background: selectedPackage?.id === pkg.id
                    ? 'rgba(255, 0, 127, 0.15)'
                    : pkg.popular
                      ? 'linear-gradient(145deg, rgba(255,167,38,0.1) 0%, rgba(255,167,38,0.05) 100%)'
                      : 'rgba(255,255,255,0.05)'
                }}
              >
                {pkg.popular && (
                  <div style={styles.popularBadge}>🔥 POPÜLER</div>
                )}

                {isVipActive() && (
                  <div style={styles.vipDiscountBadge}>VIP %10 İNDİRİM</div>
                )}

                <div style={styles.packageIconWrapper}>
                  <span style={styles.packageIcon}>{pkg.icon}</span>
                </div>

                <div style={styles.packageContent}>
                  <div style={styles.packageName}>{pkg.name}</div>
                  <div style={styles.packageDescription}>{pkg.description}</div>

                  <div style={styles.packageCoins}>
                    <span style={styles.coinsAmount}>{formatAmount(pkg.coins)}</span>
                    {pkg.bonus > 0 && (
                      <span style={styles.bonusAmount}>+{formatAmount(pkg.bonus)} BONUS</span>
                    )}
                  </div>
                </div>

                <div style={styles.packagePrice}>
                  {isVipActive() && pricing.savings ? (
                    <div style={styles.vipPricing}>
                      <span style={styles.originalPrice}>{pricing.original}</span>
                      <span style={styles.discountedPrice}>{pricing.discounted}</span>
                    </div>
                  ) : (
                    <span style={styles.priceAmount}>{formatPrice(currentPrice)}</span>
                  )}
                </div>
              </button>
            );
          })}
        </div>

        {selectedPackage && (
          <div style={styles.floatingPurchaseBar}>
            <div style={styles.selectedPackageInfo}>
              <span style={styles.selectedPackageName}>{selectedPackage.name} Paket</span>
              <span style={styles.selectedPackagePrice}>{formatPrice(getCurrentPrice(selectedPackage), isVipActive())}</span>
            </div>
            <button
              onClick={purchaseCoins}
              style={styles.purchaseButton}
            >
              Google Play ile Satın Al
            </button>
          </div>
        )}
      </div>

      {/* VIP Satın Alma Bölümü */}
      <div style={styles.vipSection}>
        <div style={styles.vipCard}>
          <div style={styles.vipBackground} />
          <div style={styles.vipContent}>
            <div style={styles.vipHeader}>
              <div style={styles.vipTitleWrapper}>
                <span style={styles.vipIcon}>👑</span>
                <div>
                  <h3 style={styles.vipTitle}>VIP Özel Üyelik</h3>
                  <p style={styles.vipSubtitle}>Kendinizi özel hissedin, sınırları kaldırın</p>
                </div>
              </div>
              <div style={styles.vipPriceBadge}>
                <span style={styles.vipPriceValue}>{currency.symbol}{currentVipPrice}</span>
                <span style={styles.vipPricePeriod}>/aylık</span>
              </div>
            </div>

            <div style={styles.vipBenefitsGrid}>
              <div style={styles.vipBenefitItem}>
                <span style={styles.benefitIcon}>✨</span>
                <span>Profilde özel taç ve efektler</span>
              </div>
              <div style={styles.vipBenefitItem}>
                <span style={styles.benefitIcon}>💎</span>
                <span>Her satın alımda %10 indirim</span>
              </div>
              <div style={styles.vipBenefitItem}>
                <span style={styles.benefitIcon}>🚀</span>
                <span>Canlı yayınlarda öne çıkma</span>
              </div>
              <div style={styles.vipBenefitItem}>
                <span style={styles.benefitIcon}>🎁</span>
                <span>Her ay 500 Jeton Hediye</span>
              </div>
            </div>

            <button
              onClick={handleVipPurchase}
              disabled={isVipActive()}
              style={{
                ...styles.vipActionButton,
                cursor: isVipActive() ? 'default' : 'pointer',
                background: isVipActive()
                  ? 'rgba(255,255,255,0.2)'
                  : 'linear-gradient(90deg, #FDB931 0%, #9f7928 100%)'
              }}
            >
              {isVipActive() ? `VIP Üyesisiniz (${getVipDaysLeft()} Gün)` : 'Şimdi VIP Ol'}
            </button>
          </div>
        </div>
      </div>


      <div style={styles.historySection}>
        <h3 style={styles.sectionTitle}>
          <span style={styles.sectionIcon}>📜</span> İşlem Geçmişi
        </h3>
        <div style={styles.historyList}>
          {transactions.map(transaction => (
            <div key={transaction.id} style={styles.historyItem}>
              <div style={{
                ...styles.historyIconWrapper,
                background: transaction.type === 'purchase' ? 'rgba(33, 150, 243, 0.2)' : transaction.type === 'earned' ? 'rgba(76, 175, 80, 0.2)' : 'rgba(255, 0, 127, 0.2)',
                color: transaction.type === 'purchase' ? '#2196f3' : transaction.type === 'earned' ? '#4caf50' : '#ff007f'
              }}>
                <span>{transaction.type === 'purchase' ? '🛒' : transaction.type === 'earned' ? '💰' : '🎁'}</span>
              </div>
              <div style={styles.historyDetails}>
                <div style={styles.historyDesc}>{transaction.description}</div>
                <div style={styles.historyDate}>
                  {transaction.timestamp.toLocaleString('tr-TR', {
                    day: 'numeric', month: 'long', hour: '2-digit', minute: '2-digit'
                  })}
                </div>
              </div>
              <div style={{
                ...styles.historyAmount,
                color: transaction.type === 'spent' ? '#ff5252' : '#4caf50'
              }}>
                {transaction.type === 'spent' ? '-' : '+'}{formatAmount(transaction.amount)}
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

const styles: { [key: string]: React.CSSProperties } = {
  container: {
    height: '100%',
    overflowY: 'auto',
    padding: '24px 16px 100px', // Increased bottom padding for nav bar
    background: 'transparent',
    color: '#fff',
    fontFamily: "'Inter', sans-serif",
    WebkitOverflowScrolling: 'touch', // Smooth scrolling for iOS
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '32px',
    flexWrap: 'wrap',
    gap: '20px',
  },
  headerContent: {
    flex: 1,
  },
  title: {
    fontSize: '28px',
    fontWeight: '700',
    margin: '0 0 8px 0',
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    background: 'linear-gradient(to right, #fff, #a5a5a5)',
    WebkitBackgroundClip: 'text',
    WebkitTextFillColor: 'transparent',
  },
  titleIcon: {
    fontSize: '28px',
  },
  subtitle: {
    color: 'rgba(255,255,255,0.6)',
    fontSize: '14px',
    margin: 0,
  },
  balanceCard: {
    background: 'rgba(255,255,255,0.1)',
    backdropFilter: 'blur(20px)',
    border: '1px solid rgba(255,255,255,0.1)',
    borderRadius: '20px',
    padding: '16px 24px',
    display: 'flex',
    alignItems: 'center',
    gap: '20px',
    boxShadow: '0 8px 32px rgba(0,0,0,0.2)',
  },
  balanceInfo: {
    textAlign: 'right',
  },
  balanceLabel: {
    display: 'block',
    fontSize: '12px',
    color: 'rgba(255,255,255,0.6)',
    marginBottom: '4px',
    fontWeight: '500',
  },
  balanceAmount: {
    fontSize: '24px',
    fontWeight: '800',
    color: '#ffd700',
    textShadow: '0 2px 10px rgba(255, 215, 0, 0.3)',
  },
  coinIconWrapper: {
    position: 'relative',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  balanceIcon: {
    fontSize: '32px',
    position: 'relative',
    zIndex: 2,
  },
  coinGlow: {
    position: 'absolute',
    width: '40px',
    height: '40px',
    background: 'rgba(255, 215, 0, 0.4)',
    filter: 'blur(15px)',
    zIndex: 1,
    borderRadius: '50%',
  },
  glassSection: {
    background: 'rgba(255,255,255,0.05)',
    backdropFilter: 'blur(10px)',
    border: '1px solid rgba(255,255,255,0.05)',
    borderRadius: '24px',
    padding: '24px',
    marginBottom: '32px',
  },
  sectionHeader: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    marginBottom: '20px',
  },
  sectionTitle: {
    fontSize: '20px',
    fontWeight: '600',
    margin: 0,
    color: '#fff',
  },
  sectionIcon: {
    fontSize: '24px',
  },
  adContent: {
    display: 'flex',
    flexDirection: 'row',
    gap: '24px',
    alignItems: 'center',
    flexWrap: 'wrap',
  },
  adLeft: {
    flex: 2,
    minWidth: '280px',
  },
  adRight: {
    flex: 1,
    minWidth: '200px',
  },
  adDescription: {
    color: 'rgba(255,255,255,0.8)',
    fontSize: '15px',
    lineHeight: '1.5',
    marginBottom: '20px',
  },
  highlight: {
    color: '#4caf50',
    fontWeight: 'bold',
  },
  adStats: {
    display: 'flex',
    alignItems: 'center',
    gap: '20px',
  },
  adStatItem: {
    display: 'flex',
    flexDirection: 'column',
  },
  adStatValue: {
    fontSize: '20px',
    fontWeight: 'bold',
    color: '#fff',
  },
  adStatLabel: {
    fontSize: '12px',
    color: 'rgba(255,255,255,0.5)',
  },
  statDivider: {
    width: '1px',
    height: '30px',
    background: 'rgba(255,255,255,0.1)',
  },
  adProgressContainer: {
    marginBottom: '12px',
    display: 'flex',
    alignItems: 'center',
    gap: '10px',
  },
  progressBar: {
    flex: 1,
    height: '6px',
    background: 'rgba(255,255,255,0.1)',
    borderRadius: '3px',
    overflow: 'hidden',
  },
  progressFill: {
    height: '100%',
    background: '#4caf50',
    transition: 'width 0.3s ease',
  },
  progressText: {
    fontSize: '12px',
    color: 'rgba(255,255,255,0.6)',
    fontWeight: '600',
  },
  watchAdButton: {
    width: '100%',
    padding: '14px',
    border: 'none',
    borderRadius: '16px',
    color: '#fff',
    fontSize: '15px',
    fontWeight: '600',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '10px',
    boxShadow: '0 4px 15px rgba(0,0,0,0.2)',
    transition: 'all 0.3s',
  },
  packagesSection: {
    marginBottom: '40px',
  },
  packagesGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fill, minmax(160px, 1fr))',
    gap: '16px',
    marginTop: '20px',
  },
  packageCard: {
    background: 'rgba(20, 20, 24, 0.6)',
    backdropFilter: 'blur(16px)',
    WebkitBackdropFilter: 'blur(16px)',
    borderRadius: '24px',
    padding: '24px 16px',
    textAlign: 'center',
    position: 'relative',
    cursor: 'pointer',
    transition: 'all 0.3s ease',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: '12px',
    color: '#fff',
    minHeight: '220px',
    border: '1px solid rgba(255,255,255,0.05)',
    boxShadow: '0 8px 20px rgba(0,0,0,0.2)',
  },
  popularBadge: {
    position: 'absolute',
    top: '-10px',
    left: '50%',
    transform: 'translateX(-50%)',
    background: 'linear-gradient(90deg, #ff512f, #dd2476)',
    color: '#fff',
    fontSize: '10px',
    fontWeight: '800',
    padding: '4px 8px',
    borderRadius: '20px',
    boxShadow: '0 2px 10px rgba(221, 36, 118, 0.4)',
    whiteSpace: 'nowrap',
  },
  vipDiscountBadge: {
    position: 'absolute',
    top: '8px',
    right: '8px',
    background: 'rgba(255, 215, 0, 0.2)',
    color: '#ffd700',
    fontSize: '9px',
    fontWeight: '700',
    padding: '2px 6px',
    borderRadius: '4px',
    border: '1px solid rgba(255, 215, 0, 0.3)',
  },
  packageIconWrapper: {
    width: '50px',
    height: '50px',
    background: 'rgba(255,255,255,0.05)',
    borderRadius: '50%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    marginTop: '10px',
  },
  packageIcon: {
    fontSize: '24px',
  },
  packageContent: {
    flex: 1,
  },
  packageName: {
    fontSize: '16px',
    fontWeight: '700',
    marginBottom: '4px',
  },
  packageDescription: {
    fontSize: '11px',
    color: 'rgba(255,255,255,0.5)',
    marginBottom: '8px',
  },
  packageCoins: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
  },
  coinsAmount: {
    fontSize: '20px',
    fontWeight: '800',
    color: '#fff',
  },
  bonusAmount: {
    fontSize: '10px',
    color: '#4caf50',
    fontWeight: '700',
    background: 'rgba(76, 175, 80, 0.1)',
    padding: '2px 6px',
    borderRadius: '4px',
    marginTop: '2px',
  },
  packagePrice: {
    width: '100%',
    padding: '8px',
    borderRadius: '12px',
    background: 'rgba(255,255,255,0.1)',
  },
  priceAmount: {
    fontSize: '15px',
    fontFamily: 'monospace',
    fontWeight: '700',
  },
  vipPricing: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
  },
  originalPrice: {
    fontSize: '11px',
    textDecoration: 'line-through',
    color: 'rgba(255,255,255,0.4)',
  },
  discountedPrice: {
    fontSize: '15px',
    color: '#ffd700',
    fontWeight: '700',
  },
  floatingPurchaseBar: {
    position: 'fixed',
    bottom: '90px', // Raised above the floating menu
    left: '50%',
    transform: 'translateX(-50%)',
    width: '90%',
    maxWidth: '500px',
    background: 'rgba(20, 20, 40, 0.95)',
    backdropFilter: 'blur(20px)',
    border: '1px solid rgba(255,255,255,0.1)',
    borderRadius: '24px',
    padding: '12px 20px',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    boxShadow: '0 10px 40px rgba(0,0,0,0.5)',
    zIndex: 100,
  },
  selectedPackageInfo: {
    display: 'flex',
    flexDirection: 'column',
  },
  selectedPackageName: {
    fontSize: '14px',
    color: 'rgba(255,255,255,0.7)',
  },
  selectedPackagePrice: {
    fontSize: '18px',
    fontWeight: 'bold',
    color: '#fff',
  },
  purchaseButton: {
    background: '#ff007f',
    color: '#fff',
    border: 'none',
    padding: '12px 28px',
    borderRadius: '24px',
    fontSize: '15px',
    fontWeight: '700',
    cursor: 'pointer',
    boxShadow: '0 4px 15px rgba(255, 0, 127, 0.4)',
    transition: 'transform 0.2s',
  },
  vipSection: {
    marginBottom: '40px',
  },
  vipCard: {
    background: 'linear-gradient(135deg, #1c1c1c 0%, #000000 100%)',
    borderRadius: '28px',
    padding: '30px',
    position: 'relative',
    overflow: 'hidden',
    border: '1px solid rgba(255, 215, 0, 0.4)',
    boxShadow: '0 10px 30px rgba(255, 215, 0, 0.15)',
  },
  vipBackground: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    background: 'radial-gradient(circle at top right, rgba(255, 215, 0, 0.15), transparent 60%)',
  },
  vipContent: {
    position: 'relative',
    zIndex: 2,
  },
  vipTitleWrapper: {
    display: 'flex',
    gap: '16px',
    alignItems: 'center',
    marginBottom: '20px',
  },
  vipTitle: {
    fontSize: '22px',
    fontWeight: '800',
    background: 'linear-gradient(to right, #fff, #ffd700)',
    WebkitBackgroundClip: 'text',
    WebkitTextFillColor: 'transparent',
    margin: 0,
  },
  vipSubtitle: {
    color: 'rgba(255,255,255,0.6)',
    fontSize: '13px',
    margin: 0,
  },
  vipHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    marginBottom: '30px',
  },
  vipPriceBadge: {
    background: 'rgba(255, 215, 0, 0.1)',
    border: '1px solid rgba(255, 215, 0, 0.3)',
    borderRadius: '12px',
    padding: '8px 12px',
    textAlign: 'center',
  },
  vipPriceValue: {
    display: 'block',
    fontSize: '18px',
    fontWeight: '700',
    color: '#ffd700',
  },
  vipPricePeriod: {
    fontSize: '11px',
    color: 'rgba(255,255,255,0.5)',
  },
  vipBenefitsGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
    gap: '16px',
    marginBottom: '30px',
  },
  vipBenefitItem: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    background: 'rgba(255,255,255,0.05)',
    padding: '12px',
    borderRadius: '12px',
    fontSize: '14px',
    color: 'rgba(255,255,255,0.9)',
  },
  benefitIcon: {
    fontSize: '18px',
  },
  vipActionButton: {
    width: '100%',
    padding: '16px',
    border: 'none',
    borderRadius: '16px',
    color: '#1a1a1a',
    fontSize: '16px',
    fontWeight: '800',
    textTransform: 'uppercase',
    letterSpacing: '1px',
    boxShadow: '0 4px 20px rgba(255, 215, 0, 0.3)',
    transition: 'transform 0.2s',
  },
  historySection: {
    paddingTop: '20px',
  },
  historyList: {
    display: 'flex',
    flexDirection: 'column',
    gap: '12px',
  },
  historyItem: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    background: 'rgba(255,255,255,0.03)',
    padding: '16px',
    borderRadius: '16px',
    gap: '16px',
  },
  historyIconWrapper: {
    width: '40px',
    height: '40px',
    borderRadius: '12px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: '20px',
  },
  historyDetails: {
    flex: 1,
  },
  historyDesc: {
    fontSize: '14px',
    color: '#fff',
    marginBottom: '4px',
  },
  historyDate: {
    fontSize: '12px',
    color: 'rgba(255,255,255,0.4)',
  },
  historyAmount: {
    fontSize: '15px',
    fontWeight: '700',
  },
  modalOverlay: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    background: 'rgba(0,0,0,0.8)',
    backdropFilter: 'blur(5px)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 1000,
    padding: '20px',
  },
  glassModal: {
    background: '#0f0f13',
    borderRadius: '24px',
    padding: '24px',
    width: '100%',
    maxWidth: '400px',
    border: '1px solid rgba(255,255,255,0.1)',
    boxShadow: '0 20px 50px rgba(0,0,0,0.5)',
  },
  modalHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '24px',
  },
  modalTitle: {
    fontSize: '20px',
    fontWeight: 'bold',
    margin: 0,
  },
  closeModalBtn: {
    background: 'transparent',
    border: 'none',
    color: 'rgba(255,255,255,0.5)',
    fontSize: '24px',
    cursor: 'pointer',
  },
  paymentMethods: {
    display: 'flex',
    flexDirection: 'column',
    gap: '12px',
    marginBottom: '24px',
  },
  paymentMethodCard: {
    display: 'flex',
    alignItems: 'center',
    gap: '16px',
    padding: '16px',
    borderRadius: '16px',
    border: '1px solid',
    color: '#fff',
    fontSize: '15px',
    cursor: 'pointer',
    transition: 'all 0.2s',
  },
  methodIcon: {
    fontSize: '24px',
  },
  orderSummary: {
    background: 'rgba(255,255,255,0.03)',
    borderRadius: '16px',
    padding: '16px',
    marginBottom: '24px',
  },
  summaryRow: {
    display: 'flex',
    justifyContent: 'space-between',
    marginBottom: '8px',
    fontSize: '14px',
    color: 'rgba(255,255,255,0.7)',
  },
  summaryValue: {
    color: '#fff',
    fontWeight: '500',
  },
  totalRow: {
    display: 'flex',
    justifyContent: 'space-between',
    marginTop: '16px',
    paddingTop: '16px',
    borderTop: '1px solid rgba(255,255,255,0.1)',
    fontSize: '16px',
    fontWeight: 'bold',
    color: '#fff',
  },
  totalAmount: {
    color: '#ff007f',
    fontSize: '20px',
  },
  confirmPaymentBtn: {
    width: '100%',
    background: 'linear-gradient(45deg, #ff007f, #ff4081)',
    color: '#fff',
    border: 'none',
    padding: '16px',
    borderRadius: '16px',
    fontSize: '16px',
    fontWeight: '700',
    cursor: 'pointer',
    boxShadow: '0 4px 15px rgba(255, 0, 127, 0.4)',
  },
  spinner: {
    width: '16px',
    height: '16px',
    border: '2px solid rgba(255,255,255,0.3)',
    borderTopColor: '#fff',
    borderRadius: '50%',
    animation: 'spin 1s linear infinite',
  }
};


// CSS Animasyonları
const styleSheet = document.createElement('style');
styleSheet.textContent = `
  @keyframes spin {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
  }
  
  @keyframes bounce {
    0%, 20%, 50%, 80%, 100% {
      transform: translateY(0);
    }
    40% {
      transform: translateY(-10px);
    }
    60% {
      transform: translateY(-5px);
    }
  }

  @keyframes shine {
    0% { transform: translateX(-100%) translateY(-100%) rotate(45deg); }
    100% { transform: translateX(100%) translateY(100%) rotate(45deg); }
  }

  @keyframes float {
    0%, 100% { transform: translateY(0px); }
    50% { transform: translateY(-3px); }
  }
`;
document.head.appendChild(styleSheet);

export default JetonPage;

